import caseProps, {
  endEventProps,
  buildDynamicJsonGroup,
} from "./parts/CaseProps";
import { is } from "bpmn-js/lib/util/ModelUtil";
import processGeneralProps from "./parts/ProcessProps";

const LOW_PRIORITY = 500;

// Champs JSON à exposer comme groupes dynamiques pour les Tasks
const TASK_JSON_GROUPS: Array<{ field: string; label: string }> = [
  { field: "params", label: "Params" },
  { field: "caseContext", label: "Case Context" },
  { field: "caseOwner", label: "Case Owner" },
  { field: "caseStatus", label: "Case Status" },
  { field: "caseRelationSettings", label: "Case Relation Settings" },
  { field: "inputs", label: "Inputs" },
  { field: "outputs", label: "Outputs" },
  { field: "initContext", label: "Init Context" },
  { field: "startCondition", label: "Start Condition" },
  { field: "resumeCondition", label: "Resume Condition" },
  { field: "dueAtDuration", label: "Due At Duration" },
  { field: "actions", label: "Actions" },
  { field: "actionParams", label: "Action Params" },
  { field: "retry", label: "Retry" },
  { field: "owner", label: "Owner" },
  { field: "group", label: "Group" },
  { field: "data", label: "Data" },
  { field: "dataTemplate", label: "Data Template" },
];

// Champs JSON pour les End Events
const END_EVENT_JSON_GROUPS: Array<{ field: string; label: string }> = [
  { field: "params", label: "Params" },
  { field: "caseStatus", label: "Case Status" },
  { field: "caseContext", label: "Case Context" },
  { field: "inputs", label: "Inputs" },
];

// ── NOUVEAU : mêmes tree-editors récursifs pour le Process ─────────────────
const PROCESS_JSON_GROUPS: Array<{ field: string; label: string }> = [
  { field: "actions", label: "Actions" },
  { field: "activityActions", label: "Activity Actions" },
  { field: "forms", label: "Forms" },
  { field: "form", label: "Form" },
  { field: "view", label: "View" },
  { field: "filterCategory", label: "Filter Category" },
  { field: "filters", label: "Filters" },
];

const CASE_ACTIVITY_TYPES = [
  "bpmn:Task",
  "bpmn:CallActivity",
  "bpmn:ExclusiveGateway",
  "bpmn:ParallelGateway",
  "bpmn:InclusiveGateway",
  "bpmn:IntermediateCatchEvent",
  "bpmn:IntermediateThrowEvent",
];

function isCaseActivityElement(element: any): boolean {
  return CASE_ACTIVITY_TYPES.some((t) => is(element, t));
}

export default function CasePropertiesProvider(
  this: any,
  propertiesPanel: any,
  translate: any,
) {
  this.getGroups = function (element: any) {
    type Group = {
      id?: string;
      label?: string;
      entries?: any[];
      tooltip?: string;
      [key: string]: any;
    };

    return function (groups: Array<Group>) {
      const generalGroup = groups.find((g) => g?.id === "general");

      // ── Task ──────────────────────────────────────────────────────────────
      if (isCaseActivityElement(element)) {
        if (generalGroup && Array.isArray(generalGroup.entries)) {
          generalGroup.entries.push(...caseProps(element));
        }

        TASK_JSON_GROUPS.forEach(({ field, label }) => {
          groups.push({
            id: `node-${field}`,
            label: translate(label),
            entries: buildDynamicJsonGroup(element, field, label),
          });
        });
      }

      // ── EndEvent ──────────────────────────────────────────────────────────
      if (is(element, "bpmn:EndEvent")) {
        if (generalGroup && Array.isArray(generalGroup.entries)) {
          generalGroup.entries.push(...endEventProps(element));
        }

        END_EVENT_JSON_GROUPS.forEach(({ field, label }) => {
          groups.push({
            id: `endEvent-${field}`,
            label: translate(label),
            entries: buildDynamicJsonGroup(element, field, label),
          });
        });
      }

      // ── Process ───────────────────────────────────────────────────────────
      if (is(element, "bpmn:Process")) {
        if (generalGroup && Array.isArray(generalGroup.entries)) {
          const nameEntryIndex = generalGroup.entries.findIndex(
            (e: any) => e.id === "name",
          );
          if (nameEntryIndex !== -1) {
            generalGroup.entries.splice(nameEntryIndex, 1);
          }
          generalGroup.entries.push(...processGeneralProps(element));
        }

        // Même éditeur récursif (obj / + sous-propriété / ×) que pour les nœuds
        PROCESS_JSON_GROUPS.forEach(({ field, label }) => {
          groups.push({
            id: `process-${field}`,
            label: translate(label),
            entries: buildDynamicJsonGroup(element, field, label),
          });
        });
      }

      return groups;
    };
  };

  propertiesPanel.registerProvider(LOW_PRIORITY, this);
}

CasePropertiesProvider.$inject = ["propertiesPanel", "translate"];
